import { isEqual } from "lodash";
import { Classroom, Day, Instructor } from "@models/ISchema";
import { BlockRanges, EditedSession } from "../context/formData.reducer";
import { Week } from "../context/linkData.reducer";

const toIds = (items: { id?: string }[] = []): string[] => items.map(item => item?.id ?? "");

/**
 * Returns true if the two given arrays of instructors have the same IDs, false
 * otherwise.
 *
 * Time complexity: O(n)
 * Where `n` is the number of elements in the `i1` or `i2` array.
 *
 * @param i1 Instructor[]
 * @param i2 Instructor[]
 * @return boolean
 */
export const sameInstructors = (i1: Instructor[], i2: Instructor[]): boolean =>
  isEqual(toIds(i1), toIds(i2));

/**
 * Returns true if the two given arrays of `checked` intervals have the same
 * IDs, false otherwise.
 *
 * Time complexity: O(n)
 * Where `n` is the number of elements in the `w1` or `w2` array.
 *
 * @param w1 Week[]
 * @param w2 Week[]
 * @return boolean
 */
export const sameIntervals = (w1: Week[], w2: Week[]): boolean => {
  const checkedIds = (weeks: Week[]) => toIds((weeks ?? []).filter(week => week.checked));
  return isEqual(checkedIds(w1), checkedIds(w2));
};

/**
 * Returns true if the two given arrays of classrooms have the same IDs, false
 * otherwise.
 *
 * Time complexity: O(n)
 * Where `n` is the number of elements in the `c1` or `c2` array.
 *
 * @param c1 Classroom[]
 * @param c2 Classroom[]
 * @return boolean
 */
export const sameClassrooms = (c1: Classroom[], c2: Classroom[]): boolean =>
  isEqual(toIds(c1), toIds(c2));

/**
 * Returns true if the two given objects of blockRanges have the same keys and
 * values, false otherwise.
 *
 * Time complexity: O(1)
 *
 * @param b1 BlockRanges
 * @param b2 BlockRanges
 * @return boolean
 */
export const sameBlocks = (b1: BlockRanges, b2: BlockRanges): boolean => {
  if (isEqual(b1?.selected, b2?.selected)) {
    if (b1?.selected === "blocks") {
      return isEqual(b1?.blocks, b2?.blocks);
    } else if (b1?.selected === "hours") {
      return (
        b1?.selected === "hours" &&
        isEqual(b1?.startTime, b2?.startTime) &&
        isEqual(b1?.endTime, b2?.endTime)
      );
    }
  }

  return !b1?.selected && !b2?.selected;
};

/**
 * Returns true if the two given days of classrooms have the same IDs, false
 * otherwise.
 *
 * Time complexity: O(1)
 *
 * @param c1 Classroom[]
 * @param c2 Classroom[]
 * @return boolean
 */
export const sameDays = (d1: Day, d2: Day): boolean => isEqual(d1, d2);

/**
 * Returns true if the two given sessions have the same resources (instructors,
 * blocks, classrooms and intervals), false otherwise.
 *
 * Time complexity: O(classrooms + instructors + intervals)
 * Where
 *   - `classrooms` is the number of elements of the longest s1.classrooms or s2.classrooms array
 *   - `instructors` is the number of elements of the longest s1.instructors or s2.instructors array
 *   - `intervals` is the number of elements of the longest s1.intervals or s2.intervals array
 *
 * @param s1 EditedSession
 * @param s2 EditedSession
 * @return boolean
 */
export const sessionsHaveTheSameResources = (s1: EditedSession, s2: EditedSession): boolean => {
  const blocksAreEqual = (b1: BlockRanges, b2: BlockRanges): boolean => {
    if (b1?.selected === b2?.selected) {
      if (b1?.selected === "blocks") {
        const isEqual = b1?.blocks === b2?.blocks;
        return isEqual;
      }

      if (b1?.selected === "hours") {
        const isEqual = b1?.startTime === b2?.startTime && b1?.endTime === b2?.endTime;
        return isEqual;
      }
    }

    const isEqual = !b1?.selected && !b2?.selected;
    return isEqual;
  };

  const daysAreEqual = (d1: Day | undefined | string, d2: Day | undefined | string): boolean => {
    const isEqual = JSON.stringify(d1) === JSON.stringify(d2);

    return isEqual;
  };

  const classroomsAreEqual = (c1: Classroom[], c2: Classroom[]): boolean => {
    if (c1.length !== c2.length) {
      return false;
    }

    const c1Ids = c1.map(c => c.id).sort();
    const c2Ids = c2.map(c => c.id).sort();

    const isEqual = c1Ids.every((value, index) => value === c2Ids[index]);
    return isEqual;
  };

  const instructorsAreEqual = (i1: Instructor[], i2: Instructor[]): boolean => {
    if (i1.length !== i2.length) {
      return false;
    }

    const i1Ids = i1.map(i => i.id).sort();
    const i2Ids = i2.map(i => i.id).sort();

    const isEqual = i1Ids.every((value, index) => value === i2Ids[index]);
    return isEqual;
  };

  const intervalsAreEqual = (w1: Week[], w2: Week[]): boolean => {
    const getCheckedIds = (weeks: Week[]): string[] => {
      return weeks
        .filter(week => week.checked)
        .map(week => week.id)
        .sort();
    };

    const w1CheckedIds = getCheckedIds(w1);
    const w2CheckedIds = getCheckedIds(w2);

    if (w1CheckedIds.length !== w2CheckedIds.length) {
      return false;
    }

    const isEqual = w1CheckedIds.every((value, index) => value === w2CheckedIds[index]);
    return isEqual;
  };

  const s1Blocks = s1.blocks || {};
  const s2Blocks = s2.blocks || {};

  const s1Days = s1.blocks?.day;
  const s2Days = s2.blocks?.day;

  const s1Classrooms = s1.classrooms || [];
  const s2Classrooms = s2.classrooms || [];

  const s1Instructors = s1.instructors || [];
  const s2Instructors = s2.instructors || [];

  const s1Intervals = s1.intervals || [];
  const s2Intervals = s2.intervals || [];

  return (
    blocksAreEqual(s1Blocks, s2Blocks) &&
    daysAreEqual(s1Days, s2Days) &&
    classroomsAreEqual(s1Classrooms, s2Classrooms) &&
    instructorsAreEqual(s1Instructors, s2Instructors) &&
    intervalsAreEqual(s1Intervals, s2Intervals)
  );
};
